import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const useShop = () => {
  const { api, bearerApi, AdminId, shops, setShops } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // FETCH SHOPS
  // Original API used Basic token, so use api
  const fetchShops = async () => {
    setLoading(true);

    try {
      const res = await api.post("/api/Shop/v1.0/getShopList", {
        adminId: String(AdminId),
      });

      if (res.data?.responseCode === "00") {
        const list = res.data?.data || [];
        setShops(list);
        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch shops");
      }

      setShops([]);
      return [];
    } catch (err) {
      console.error("fetchShops error:", err);
      toast.error("Error fetching shops");
      setShops([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ADD SHOP
  // Original API used Basic token, so use api
  const addShop = async (payload) => {
    try {
      const res = await api.post("/api/Shop/v1.0/onboardShop", payload);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop added");
        await fetchShops();
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to add shop");
      }

      return false;
    } catch (err) {
      console.error("addShop error:", err);
      toast.error("Error adding shop");
      return false;
    }
  };

  // DELETE SHOP
  // Original API used Bearer token, so use bearerApi
  const deleteShop = async (shopId, onSuccess) => {
    if (!shopId) {
      toast.error("Invalid shop ID");
      return false;
    }

    try {
      const res = await bearerApi.post("/api/Shop/v1.0/deleteShopDetails", {
        shopId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop deleted");
        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete shop");
      }

      return false;
    } catch (err) {
      console.error("deleteShop error:", err);
      toast.error("Error deleting shop");
      return false;
    }
  };

  // UPDATE SHOP
  // Original API used Bearer token, so use bearerApi
  const updateShop = async (payload, onSuccess) => {
    try {
      const res = await bearerApi.post(
        "/api/Shop/v1.0/updateShopDetails",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop updated");
        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update shop");
      }

      return false;
    } catch (err) {
      console.error("updateShop error:", err);
      toast.error("Error updating shop");
      return false;
    }
  };

  // FETCH LAT/LONG
  // Original API used Basic token, so use api
  const fetchLatLong = async (address) => {
    if (!address) {
      toast.error("Address is required");
      return {
        latitude: "",
        longitude: "",
      };
    }

    try {
      const res = await api.post("/api/Task/v1.0/getLatLongs", {
        address,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        const d = res.data?.data || {};

        return {
          latitude: d.latitude || d.lat || "",
          longitude: d.longitude || d.lng || "",
        };
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch location");
      }

      return {
        latitude: "",
        longitude: "",
      };
    } catch (err) {
      console.error("fetchLatLong error:", err);
      toast.error("Error fetching location");

      return {
        latitude: "",
        longitude: "",
      };
    }
  };

  // UPDATE SHOP STATUS
  // Original API used Bearer token, so use bearerApi
  const updateShopStatus = async ({ shopId, status, onSuccess }) => {
    if (!shopId) {
      toast.error("Invalid shop ID");
      return false;
    }

    setStatusUpdatingId(shopId);

    try {
      const res = await bearerApi.post("/api/Shop/v1.0/updateShopStatus", {
        adminId: String(AdminId),
        shopId: String(shopId),
        status: String(status),
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          status === "1"
            ? "Shop activated successfully"
            : "Shop set offline successfully"
        );

        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update shop status");
      }

      return false;
    } catch (err) {
      console.error("updateShopStatus error:", err);
      toast.error("Error updating shop status");
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  return {
    shops,
    setShops,
    loading,
    fetchShops,
    addShop,
    updateShop,
    deleteShop,
    fetchLatLong,
    updateShopStatus,
    statusUpdatingId,
  };
};

export default useShop;